import { memo } from 'react';
import ReactMarkdown, { type Options } from 'react-markdown';
import rehypeExternalLinks from 'rehype-external-links';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import { FlexBox, type FlexBoxProps } from '@blockworks/ui/flex-box';
import { cn } from '@blockworks/ui/system';
import { Text, type TextProps } from '@blockworks/ui/text';

type MarkdownRendererProps = {
    markdown?: string;
    showNoContentPlaceholder?: boolean;
    bodyFontSize?: TextProps['size'];
} & Omit<Options, 'components' | 'rehypePlugins'>;

const rehypePlugins: Options['rehypePlugins'] = [
    rehypeRaw,
    rehypeSanitize,
    remarkGfm,
    [rehypeExternalLinks, { rel: ['nofollow'], target: '_blank' }],
];

const MarkdownRenderer = ({
    showNoContentPlaceholder = false,
    markdown,
    bodyFontSize = 'sm',
    ...rest
}: MarkdownRendererProps) => {
    if (!markdown && showNoContentPlaceholder) {
        return (
            <FlexBox w="full" h="full" col center>
                <Text as="span" color="deselect" size="lg" weight="semibold" lineHeight={1}>
                    No Content.
                </Text>
                <Text as="span" color="deselect" size="sm">
                    Start writing to preview your content.
                </Text>
            </FlexBox>
        );
    }
    return (
        <ReactMarkdown
            rehypePlugins={rehypePlugins}
            {...rest}
            className={cn(
                'prose',
                'prose-headings:leading-[1.1]',
                'prose-headings:!mb-1',
                'prose-headings:!mt-0',
                'prose-headings-tight',
                'light:prose-p:text-content',
                'prose-p:leading-tight',
                'prose-p:mb-0',
                'prose-strong:text-content',
                {
                    'prose-p:text-xs prose-li:text-xs': bodyFontSize === 'xs',
                    'prose-p:text-sm prose-li:text-sm': bodyFontSize === 'sm',
                },
                'prose-a:text-[length:inherit]',
                'prose-a:text-primary prose-a:hover:text-primary-highlight',
                'prose-li:my-0.5',
                'light:prose-i:text-content',
                'light:prose-li:text-content',
                'prose-li:leading-tight',
                'prose-img:mt-4 prose-img:mb-0',
                'prose-img:inline-block',
            )}
        >
            {markdown}
        </ReactMarkdown>
    );
};

type WrapperProps = {
    children: React.ReactNode;
    withScroller?: boolean;
} & FlexBoxProps;

const Wrapper = ({ children, withScroller = false, border, borderRadius = 'sm', className, ...rest }: WrapperProps) => {
    return (
        <FlexBox
            w="full"
            h="full"
            col
            className={cn({ 'scroller overflow-x-hidden': withScroller }, 'max-h-full p-[0.65rem]', className)}
            border={border}
            borderRadius={borderRadius}
            {...rest}
        >
            {children}
        </FlexBox>
    );
};

/**
 * A memoized `MarkdownRenderer` that will only re-render when the `markdown` prop changes.
 **/
MarkdownRenderer.Memoized = memo(MarkdownRenderer, (prev, next) => prev.markdown === next.markdown);
/**
 * A wrapper (or container) element to help with spacing and scrolling.
 **/
MarkdownRenderer.Wrapper = Wrapper;

export type { MarkdownRendererProps };
export { MarkdownRenderer };
